import { deviceDetector } from "../uaDevicesParse/deviceDetector";

// 统一的 Firebase 配置 - Edgen 项目

export interface FirebaseAuthConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

/**
 * 根据环境检测结果选择正确的 authDomain
 * 统一处理 iOS PWA 和原生应用的 authDomain 选择逻辑
 * @param baseConfig 包含 authDomain 和可选 authDomainIOSPWA 的配置对象
 * @returns 正确的 authDomain 值
 */
export const selectAuthDomain = <
  T extends { authDomain: string; authDomainIOSPWA?: string },
>(
  baseConfig: T,
): string => {
  // 动态导入 deviceDetector 以避免循环依赖
   

  // 检测是否为 iOS App 环境或原生应用（包括 cookie 标记）
  const isApp = deviceDetector.isApp();
  const shouldUseIOSPWADomain = isApp && baseConfig.authDomainIOSPWA;

  return shouldUseIOSPWADomain
    ? baseConfig.authDomainIOSPWA!
    : baseConfig.authDomain;
};

// Edgen Firebase 配置
interface FirebaseConfigWithIOSPWA {
  apiKey: string;
  authDomain: string;
  authDomainIOSPWA?: string; // iOS PWA 环境专用的 authDomain（如 ngrok 域名）
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

// ⚠️ authDomain 自定义域名（2026-07-20）
// Google/Apple 已配新域名 callback（GCP OAuth redirect URI / Apple Services ID Return URLs）。
// X(Twitter) 未配：Twitter Developer App Callback URLs 尚未更新，X 登录暂不可用。
// 如需还原（因 X 登录）：authDomain 改回默认值 — dev/beta=edgen-beta.firebaseapp.com, pre=edgen-pre.firebaseapp.com, prod=edgen-prod.firebaseapp.com
export const edgenFirebaseConfig: Record<string, FirebaseConfigWithIOSPWA> = {
  // dev: {
  //   apiKey: "AIzaSyBZ0yotUor_wP9A19g9oOQDi0JIrCsukQ4",
  //   authDomain: "edgen-dev-f9d7d.firebaseapp.com",
  //   authDomainIOSPWA: "edgendev.vercel.app", // 可选：iOS PWA 环境使用
  //   projectId: "edgen-dev-f9d7d",
  //   storageBucket: "edgen-dev-f9d7d.firebasestorage.app",
  //   messagingSenderId: "754677500332",
  //   appId: "1:754677500332:web:33b863849ac389e6afd209",
  //   measurementId: "G-17XLVT31YZ",
  // },
  dev: {
    apiKey: "AIzaSyB4ZLj-t2OWtF8KKdq4LfPB3ME9BdtRcxU",
    authDomain: "ed.beta.edwealth.ai",
    authDomainIOSPWA: "ed.beta.edwealth.ai",
    projectId: "edgen-beta",
    storageBucket: "edgen-beta.firebasestorage.app",
    messagingSenderId: "471447669771",
    appId: "1:471447669771:web:673b8039e91ee0dba94b0e",
    measurementId: "G-NTXFLHQ0DF",
  },
  beta: {
    apiKey: "AIzaSyB4ZLj-t2OWtF8KKdq4LfPB3ME9BdtRcxU",
    authDomain: "ed.beta.edwealth.ai",
    authDomainIOSPWA: "ed.beta.edwealth.ai",
    projectId: "edgen-beta",
    storageBucket: "edgen-beta.firebasestorage.app",
    messagingSenderId: "471447669771",
    appId: "1:471447669771:web:673b8039e91ee0dba94b0e",
    measurementId: "G-NTXFLHQ0DF",
  },
  pre: {
    apiKey: "AIzaSyAjrapehUVyYbb6q8VTqNnqGmKdBAwYSgM",
    authDomain: "auth-pre.edwealth.ai",
    authDomainIOSPWA: "ed.pre.edwealth.ai", // 可选：iOS PWA 环境使用
    projectId: "edgen-pre",
    storageBucket: "edgen-pre.firebasestorage.app",
    messagingSenderId: "367137857142",
    appId: "1:367137857142:web:77eac58549198416328957",
    measurementId: "G-60R2H0VB2W",
  },
  prod: {
    apiKey: "AIzaSyDmo0xVgG5QpBwu3NP18FTkh6Lx3M4cC8k",
    authDomain: "auth.edwealth.ai",
    authDomainIOSPWA: "www.edwealth.ai", // 可选：iOS PWA 环境使用
    projectId: "edgen-prod",
    storageBucket: "edgen-prod.firebasestorage.app",
    messagingSenderId: "1065094501140",
    appId: "1:1065094501140:web:fcc1a7857b51b2cd2a2801",
    measurementId: "G-1LJC5P2LD7",
  },
};

// Edgen VAPID Key 配置
export const edgenVapidKeyConfig = {
  dev: "BMm5DELhMFrOGlsk2GsAk22r63cRdp16jFmNfDkccud7O3GYf1vMITgy3nLlSW_vqM1q4kUQLBKwRmpMhWao17w",
  beta: "BPJBTgCPGsvzA4ActdRK18noRYWuk7wlBjZOSw91A7LtNJlYDoQDp7ip5nH3HabXAaNGpq1zo252U3I1nykfZsE",
  pre: "BCut5EPoadD6CR8Fp2wjk1LCtBOW0KJad0i0-W4h7s1jPYoG-TXuyHKOjpBzOG4Ii2MnG4H3uE3yZ2ba2BsDcaA",
  prod: "BEP5-Pepb2UNf7k0xXsmie1PYwu3a4ldTJ5v2lhpMvWnbSk1rvCAc3veMDmLVICduqli_AJF1O7YngJM_QqPNkY",
};

// 获取当前环境的 Firebase 配置
export const getFirebaseConfig = (): FirebaseAuthConfig => {
  const env = process.env.NEXT_PUBLIC_ENV as keyof typeof edgenFirebaseConfig;
  const baseConfig = edgenFirebaseConfig[env];

  // 使用统一的 authDomain 选择逻辑
  const authDomain = selectAuthDomain(baseConfig);

  // 返回标准格式的配置（移除 authDomainIOSPWA 字段）
  return {
    apiKey: baseConfig.apiKey,
    authDomain,
    projectId: baseConfig.projectId,
    storageBucket: baseConfig.storageBucket,
    messagingSenderId: baseConfig.messagingSenderId,
    appId: baseConfig.appId,
    measurementId: baseConfig.measurementId,
  };
};

// 获取当前环境的 VAPID Key 配置
export const getVapidKey = () => {
  const env = process.env.NEXT_PUBLIC_ENV as keyof typeof edgenVapidKeyConfig;
  return edgenVapidKeyConfig[env] || edgenVapidKeyConfig.beta;
};
